import type {
  EventElasticSearchIdSource,
  EventElasticSearchSummarySource,
  EventSearchIdHit,
  EventSearchSummariesResult,
  EventSearchSummary,
} from '@src/models/interfaces/event.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { DateUtil } from '@src/packages/utils/dates';

export class EventMapper {
  static fromElasticSearchIdHit = (
    hit: ElasticSearchHit<EventElasticSearchIdSource>,
  ): EventSearchIdHit | null => {
    const id = hit._source?.id ?? hit._id;
    const divisionId = hit._source?.division?.id;

    if (!id || !divisionId) {
      return null;
    }

    return { id, divisionId };
  };

  static fromElasticSearchIdsResponse = (
    response: ElasticSearchResponse<EventElasticSearchIdSource> | null,
  ): { items: EventSearchIdHit[]; total: number; idsByDivision: Record<string, string[]> } => {
    const hits = response?.hits?.hits ?? [];
    const items = hits
      .map(hit => EventMapper.fromElasticSearchIdHit(hit))
      .filter((item): item is EventSearchIdHit => item != null);

    const idsByDivision = items.reduce<Record<string, string[]>>((acc, item) => {
      (acc[item.divisionId] ??= []).push(item.id);
      return acc;
    }, {});

    return {
      items,
      total: response?.hits?.total?.value ?? items.length,
      idsByDivision,
    };
  };

  static fromElasticSearchSummaryHit = (
    hit: ElasticSearchHit<EventElasticSearchSummarySource>,
  ): EventSearchSummary | null => {
    const source = hit._source ?? {};
    const id = source.id ?? hit._id;
    const divisionId = source.division?.id;

    if (!id || !divisionId) {
      return null;
    }

    return {
      id,
      createdAtMs: DateUtil.getTimestampMs(source.createdAtDevice),
      division: { id: divisionId, name: source.division?.name },
      user: source.user,
      eventType: source.eventType,
    };
  };

  static fromElasticSearchSummariesResponse = (
    response: ElasticSearchResponse<EventElasticSearchSummarySource> | null,
  ): EventSearchSummariesResult => {
    const items = (response?.hits?.hits ?? [])
      .map(hit => EventMapper.fromElasticSearchSummaryHit(hit))
      .filter((item): item is EventSearchSummary => item != null);

    return { items, total: response?.hits?.total?.value ?? items.length };
  };
}
